import React from 'react';
import { Compass, Lock, Calendar, ChevronRight, CheckCircle } from 'lucide-react';
import { getModuleIcon } from './DashboardShared';

const domainCardClass = { uyku: 'plan-module--sleep', kaygi: 'plan-module--anxiety', ruh_hali: 'plan-module--mood', enerji: 'plan-module--energy', dikkat: 'plan-module--energy', bedensel: 'plan-module--energy', gunluk_islev: 'plan-module--mood' };
const domainIconClass = { uyku: 'plan-icon--sleep', kaygi: 'plan-icon--anxiety', ruh_hali: 'plan-icon--mood', enerji: 'plan-icon--energy', dikkat: 'plan-icon--energy', bedensel: 'plan-icon--energy', gunluk_islev: 'plan-icon--mood', bilissel: 'plan-icon--cognitive', kalp_dolasim: 'plan-icon--cardiovascular' };
const domainChevron = { uyku: { c: '#67E8F9', g: 'drop-shadow(0 0 8px rgba(56,189,248,0.45))' }, kaygi: { c: '#FCD34D', g: 'drop-shadow(0 0 8px rgba(245,158,11,0.45))' }, ruh_hali: { c: '#C084FC', g: 'drop-shadow(0 0 8px rgba(168,85,247,0.45))' } };

const AssessmentPlanPanel = ({ userPlan, onAssessmentClick }) => (
  <div className="panel-plan" data-testid="roadmap-section">
    <div className="panel-plan-ambient" />

    {/* Title */}
    <div className="flex items-center justify-between mb-5" style={{ position: 'relative', zIndex: 1 }}>
      <div className="flex items-center gap-3">
        <div style={{
          width: '34px', height: '34px', borderRadius: '999px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(168,85,247,0.10)', border: '1px solid rgba(196,181,253,0.22)',
          boxShadow: '0 0 12px rgba(168,85,247,0.16), inset 0 0 8px rgba(255,255,255,0.03)',
        }}>
          <Compass className="w-4 h-4" style={{ color: '#C084FC', filter: 'drop-shadow(0 0 6px rgba(168,85,247,0.6))' }} />
        </div>
        <h3 style={{ color: '#F8FAFC', fontWeight: 700, fontSize: '18px', lineHeight: 1.2, letterSpacing: '-0.02em' }}>Sıradaki Değerlendirmeler</h3>
      </div>
      <ChevronRight className="w-4 h-4" style={{ color: '#64748B' }} />
    </div>

    {/* Assessment items */}
    {userPlan?.assessments?.length > 0 ? (
      <div className="space-y-3" style={{ position: 'relative', zIndex: 1 }}>
        {userPlan.assessments.map((item, i) => {
          const ModIcon = getModuleIcon(item.domain);
          const isActive = item.item_state === 'active';
          const isNext = item.item_state === 'next';
          const isLocked = item.item_state === 'locked';
          const isCompleted = item.item_state === 'completed';
          const badgeColors = {
            active: { bg: domainChevron[item.domain]?.c ? `${domainChevron[item.domain].c}18` : 'rgba(245,158,11,0.12)', color: domainChevron[item.domain]?.c || '#FCD34D' },
            next: { bg: 'rgba(148,163,184,0.10)', color: '#94A3B8' },
            locked: { bg: 'rgba(100,116,139,0.08)', color: '#64748B' },
            completed: { bg: 'rgba(52,211,153,0.12)', color: '#34D399' },
          };
          const badge = badgeColors[item.item_state] || badgeColors.locked;

          const canClick = !isLocked && !isCompleted && (item.test_id || item.is_cardio);

          return (
            <div key={i} className={`plan-module ${isLocked ? 'plan-module--locked' : isCompleted ? 'plan-module' : isActive ? (domainCardClass[item.domain] || 'plan-module--energy') : 'plan-module'}`}
              style={{ 
                ...isNext ? { opacity: 0.72 } : {},
                ...(isCompleted ? { opacity: 0.7 } : {}),
                cursor: canClick ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (canClick && onAssessmentClick) {
                  onAssessmentClick(item);
                }
              }}
              data-testid={`assessment-${item.item_state}-${i}`}>
              <div className={`plan-icon ${isLocked ? 'plan-icon--locked' : (domainIconClass[item.domain] || 'plan-icon--energy')}`}>
                {isLocked ? <Lock className="w-4 h-4" /> : isCompleted ? <CheckCircle className="w-5 h-5" style={{ color: '#34D399' }} /> : <ModIcon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <p style={{ color: isLocked ? '#CBD5E1' : isCompleted ? '#94A3B8' : '#F8FAFC', fontSize: '16px', fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.01em' }} className="truncate">{item.label}</p>
                  <span className="px-2 py-0.5 rounded text-[10px] font-semibold shrink-0" style={{ background: badge.bg, color: badge.color }}>{item.status_label}</span>
                </div>
                <p style={{ color: isLocked || isCompleted ? '#94A3B8' : '#AAB4C5', fontSize: '13px', lineHeight: 1.35 }} className="truncate">
                  {isLocked ? (item.reason || 'Bu alan birazdan açılacak') : item.description}
                </p>
              </div>
              {isCompleted ? (
                <CheckCircle className="w-4 h-4 shrink-0" style={{ color: '#34D399' }} />
              ) : (
                <ChevronRight className="w-4 h-4 shrink-0" style={{
                  color: isLocked ? '#64748B' : isNext ? '#94A3B8' : (domainChevron[item.domain]?.c || '#FCD34D'),
                  filter: isActive ? (domainChevron[item.domain]?.g || '') : '',
                }} />
              )}
            </div>
          );
        })}
      </div>
    ) : userPlan?.working_together?.length > 0 && (
      <div className="space-y-3 mb-4" style={{ position: 'relative', zIndex: 1 }}>
        {userPlan.working_together.map((mod, i) => {
          const ModIcon = getModuleIcon(mod.domain);
          const ds = { uyku: 'plan-module--sleep', kaygi: 'plan-module--anxiety', ruh_hali: 'plan-module--mood' };
          const di = { uyku: 'plan-icon--sleep', kaygi: 'plan-icon--anxiety', ruh_hali: 'plan-icon--mood', bilissel: 'plan-icon--cognitive', kalp_dolasim: 'plan-icon--cardiovascular' };
          return (
            <div key={i} className={`plan-module ${ds[mod.domain] || 'plan-module--energy'}`} data-testid={`module-active-${i}`}>
              <div className={`plan-icon ${di[mod.domain] || 'plan-icon--energy'}`}><ModIcon className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <p style={{ color: '#F8FAFC', fontSize: '16px', fontWeight: 700 }} className="truncate">{mod.label}</p>
                <p style={{ color: '#AAB4C5', fontSize: '13px' }} className="truncate">{mod.description}</p>
              </div>
            </div>
          );
        })}
      </div>
    )}

    {/* Next reassessment */}
    {userPlan?.next_reassessment?.next_date && (
      <div className="mt-5 pt-3 flex items-center gap-2" style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Calendar className="w-3.5 h-3.5" style={{ color: '#94A3B8' }} />
        <span style={{ color: '#94A3B8', fontSize: '13px' }}>Sonraki değerlendirme: {userPlan.next_reassessment.next_date?.slice(0, 10)}</span>
      </div>
    )}

    {/* Bottom LED line */}
    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(167,139,250,0.35), transparent)', boxShadow: '0 0 8px rgba(167,139,250,0.15)' }} />
  </div>
);

export default AssessmentPlanPanel;
